<script setup lang="ts">
import type { Dropdown as VDropdown } from "floating-vue";
import { vInfiniteScroll } from "@vueuse/components";
import get from "lodash-es/get";

import type { UInputControl, UListItem, UMenu } from "#build/components";
import type { ComputedRef } from "#imports";
import type { PxlIcon } from "@/common/components/U/Icon";
import type { inputSizes, inputVariants } from "@/common/components/U/Input";
import getLightness from "@/common/helpers/get-lightness";
import { generateUUID } from "~/common/helpers/uuid";

type VDropdownPlacement = InstanceType<typeof VDropdown>["$props"]["placement"];

// TODO: Move into separate file when such feature will be supported
interface ICommonInputProps {
  name?: string;
  placeholder?: string;
  required?: boolean;
  loading?: boolean;
  disabled?: boolean;
  icon?: PxlIcon;
  leadingIcon?: PxlIcon;
  trailingIcon?: PxlIcon | false;
  trailing?: boolean;
  leading?: boolean;
  hasError?: boolean;
  size?: keyof typeof inputSizes;
  variant?: keyof typeof inputVariants;
  brandingColor?: string;
  truncateDisplayValue?: boolean;
  ariaLabel?: string;
  hasEmptyState?: boolean;
}

export interface SelectMenuOption {
  [key: string]: any;
  disabled?: boolean;
  icon?: PxlIcon;
  children?: SelectMenuOption[];
  image?: {
    src?: string;
    alt?: string;
  };
}

type InputValue = Date | string[] | string | number | number[] | null;

export interface AsyncOptionsLoadParams {
  q?: string;
  offset: number;
  limit?: number;
  parent?: string;
}

interface SelectMenuProps extends ICommonInputProps {
  modelValue?: InputValue;
  options:
    | unknown[]
    | ((params: AsyncOptionsLoadParams) => Promise<unknown[]>)
    | ((params: AsyncOptionsLoadParams) => Promise<{ data: unknown[]; total: number }>);

  valueAttribute?: string;
  optionAttribute?: string;

  limit?: number;
  asyncSearch?: boolean;
  searchDelay?: number;
  searchable?: boolean;
  nullable?: boolean;
  multiple?: boolean;

  showPlaceholderOption?: boolean;
  showSelectedOptionIcon?: boolean;
  menuPlacement?: VDropdownPlacement;
  listClass?: string;
  containerClass?: string;
  inputClass?: string;
  inputInnerClass?: string;
  itemClass?: string;
  highlightedValue?: string | null;
  dropdown?: (typeof VDropdown)["$props"];
  autoHide?: boolean;
  divider?: boolean;
  hideTrailingIcon?: boolean;
  listItemSize?: InstanceType<typeof UListItem>["$props"]["size"];
  renderInPortal?: boolean;
  totalArrayOptions?: number;
  isLoadingOptions?: boolean;
  selectAllAsItemList?: boolean;
  showSelectedOptionsLabels?: boolean;
  maxVisibleSelections?: number;
}

const { t } = useI18n();

const props = withDefaults(defineProps<SelectMenuProps>(), {
  size: "lg",
  variant: "default",
  modelValue: "",
  options: () => [],
  limit: 20,
  searchDelay: 500,
  optionAttribute: "label",
  valueAttribute: "value",
  menuPlacement: "bottom-start",
  highlightedValue: "",
  autoHide: true,
  trailingIcon: undefined,
  divider: true,
  listItemSize: "lg",
  renderInPortal: true,
  isLoadingOptions: false,
  hasEmptyState: true,
  selectAllAsItemList: false,
  showSelectedOptionsLabels: false,
  maxVisibleSelections: 3,
});

const emit = defineEmits(["update:modelValue", "focus", "blur", "search", "scroll"]);

const { emitFormChange, emitFormBlur, formGroup } = useFormGroup();
const inputId = computed(() => formGroup?.name.value || props.name);
const inputControlId = computed(() => `select-menu-control-${generateUUID()}`);
const hasFocus = ref(false);
const searchValue = ref("");
const totalOptions = ref(0);
const listItems = ref<InstanceType<typeof UListItem>[]>([]);
const menuRef = ref<InstanceType<typeof UMenu>>();

const {
  state: asyncOptions,
  isLoading: isLoadingAsyncOptions,
  error: isAsyncOptionsError,
  execute: executeAsyncOptionsLoad,
} = useAsyncState((params: AsyncOptionsLoadParams) => onAsyncOptionsLoad(params), [], { resetOnExecute: false, immediate: false });

const isLoading = computed(() => props.loading);
const isDisabled = computed(() => isLoadingAsyncOptions.value || props.loading || props.disabled || !!formGroup?.disabled?.value);
const isAllOptionsSelected = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return filteredOptions.value.every((option) => isOptionSelected(option));
  }

  return !!props.modelValue;
});

const isAnyOptionSelected = computed(() => {
  if (Array.isArray(props.modelValue)) {
    return filteredOptions.value.some((option) => isOptionSelected(option));
  }

  return !!props.modelValue;
});

const brandColor = computed(() => {
  if (props.brandingColor) {
    const color = getLightness(props.brandingColor) > 50 ? "black" : "white";

    return `!text-${color}`;
  }

  return undefined;
});

const getTrailingIcon = useMemoize((shown: boolean, disabled: boolean, variant: string, brandColor: string): PxlIcon | null => {
  if (props.trailingIcon === false) return null;
  if (props.trailingIcon) return props.trailingIcon;
  return {
    name: "chevron-down-small" as const,
    class: [
      disabled ? "cursor-default" : "cursor-pointer",
      "duration-200 transition transform",
      disabled ? "text-neutral-light-400 dark:text-neutral-dark-400" : variant === "blurry" ? "text-current" : "text-black dark:text-white",
      shown && !disabled && "rotate-180",
      brandColor,
    ].filter(Boolean),
  };
});

const guessOptionValue = (option: any) => get(option, props.valueAttribute, get(option, props.optionAttribute));
const guessOptionText = (option: any) => get(option, props.optionAttribute, get(option, props.valueAttribute));

const normalizedOptions = computed(() => {
  if (Array.isArray(props.options)) {
    return props.options.map((option) => normalizeOption(option));
  }

  return asyncOptions.value;
});

const normalizedOptionsWithPlaceholder: ComputedRef<SelectMenuOption[]> = computed(() => {
  if (!props.placeholder || !props.showPlaceholderOption) return normalizedOptions.value;

  return [{ [props.valueAttribute]: "", [props.optionAttribute]: props.placeholder, disabled: !props.nullable }, ...normalizedOptions.value];
});

const filteredOptions: ComputedRef<SelectMenuOption[]> = computed(() => {
  if (searchValue.value) {
    if (props.asyncSearch) return asyncOptions.value;
    return useSearchInArray(normalizedOptions.value, { q: searchValue.value, key: props.optionAttribute });
  }

  return normalizedOptionsWithPlaceholder.value;
});

const normalizedSelectedOption = computed<null | SelectMenuOption | SelectMenuOption[]>(() => {
  if (Array.isArray(props.modelValue)) {
    const normalizeModelValues = props.modelValue.map(normalizeOption);

    return normalizeModelValues.map((value) => {
      const foundOption = normalizedOptionsWithPlaceholder.value.find((option) => option[props.valueAttribute] === value[props.valueAttribute]);

      if (!foundOption) {
        value[props.optionAttribute] = "-";
        return value;
      }

      return foundOption;
    });
  }

  const normalizeModelValue = normalizeOption(props.modelValue);
  return normalizedOptionsWithPlaceholder.value.find((option) => option[props.valueAttribute] === normalizeModelValue[props.valueAttribute]) || null;
});

const normalizedSelectedOptionIcon = computed<null | SelectMenuOption["icon"]>(() => {
  if (Array.isArray(normalizedSelectedOption.value) || !normalizedSelectedOption.value) {
    return null;
  }

  return normalizedSelectedOption.value.icon;
});

const hiddenSelections = computed(() => {
  if (!Array.isArray(normalizedSelectedOption.value) || !props.showSelectedOptionsLabels) return [];
  return normalizedSelectedOption.value.slice(props.maxVisibleSelections);
});

const hiddenSelectionsCount = computed(() => hiddenSelections.value.length);
const hiddenSelectionsTooltip = computed(() => hiddenSelections.value.map((option) => option[props.optionAttribute]).join(", "));

const normalizedLabel = computed<string>(() => {
  const selectedOption = normalizedSelectedOption.value;

  if (Array.isArray(selectedOption)) {
    if (selectedOption.length >= 1) {
      if (props.showSelectedOptionsLabels) {
        return selectedOption
          .slice(0, props.maxVisibleSelections)
          .map((option) => option[props.optionAttribute])
          .join(", ");
      }
      return t("labels.selected_options", { number: selectedOption.length });
    }
    return "";
  }

  return selectedOption ? selectedOption[props.optionAttribute] : "";
});

const normalizedValue = computed<string | string[]>(() => {
  const selectedOption = normalizedSelectedOption.value;

  if (Array.isArray(selectedOption)) {
    return selectedOption.map((option) => option[props.valueAttribute]);
  }

  return selectedOption ? selectedOption[props.valueAttribute] : "";
});

const dropdownConfig = computed(() =>
  Object.assign(
    {
      placement: props.menuPlacement,
      autoSize: props.variant !== "inline",
      strategy: "absolute",
      noAutoFocus: true,
    },
    props.dropdown || {}
  )
);

watch(
  () => props.highlightedValue,
  () => {
    scrollHighlightValueIntoView();
  },
  { immediate: true }
);

function scrollHighlightValueIntoView(smooth: boolean = true) {
  const highlightedOptionIndex = filteredOptions.value.findIndex((option) => isOptionHighlighted(option));

  const listItem = listItems.value[highlightedOptionIndex];

  if (listItem?.$el) {
    listItem.$el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "center", inline: "center" });
  }
}

function onBlur(event: FocusEvent) {
  hasFocus.value = false;
  emitFormBlur();
  emit("blur", event);
}

function onFocus(event: FocusEvent) {
  hasFocus.value = true;
  emit("focus", event);
}

function onOptionSelect(option: SelectMenuOption) {
  const optionValue = guessOptionValue(option);

  if (props.multiple) {
    if (isOptionSelected(option)) {
      if (Array.isArray(props.modelValue)) {
        emit(
          "update:modelValue",
          props.modelValue.filter((value) => value !== optionValue)
        );
      } else {
        emit("update:modelValue", []);
      }
    } else {
      if (Array.isArray(props.modelValue)) {
        emit("update:modelValue", [...props.modelValue, optionValue]);
      } else {
        emit("update:modelValue", [optionValue]);
      }
    }
  } else {
    if (props.nullable && isOptionSelected(option)) {
      emit("update:modelValue", "");
    } else {
      emit("update:modelValue", optionValue);
    }
  }

  emitFormChange();
}

function clearSelection() {
  if (props.modelValue) {
    emit("update:modelValue", []);
  } else {
    emit("update:modelValue", "");
  }

  emitFormChange();
}

function normalizeOption(option: any) {
  if (["string", "number", "boolean", "undefined", "bigint", "symbol", "function"].includes(typeof option)) {
    return {
      [props.valueAttribute]: option,
      [props.optionAttribute]: option,
    };
  }

  return {
    ...option,
    [props.valueAttribute]: guessOptionValue(option),
    [props.optionAttribute]: guessOptionText(option),
  };
}

function isOptionSelected(option: SelectMenuOption): boolean {
  const selectedValue = normalizedValue.value;
  if (Array.isArray(selectedValue)) {
    return !!selectedValue.find((value) => option[props.valueAttribute] === value);
  }

  return option[props.valueAttribute] === selectedValue;
}

function isOptionHighlighted(option: SelectMenuOption): boolean {
  const selectedValue = props.highlightedValue;
  if (Array.isArray(selectedValue)) {
    return !!selectedValue.find((value) => option[props.valueAttribute] === value);
  }

  return option[props.valueAttribute] === selectedValue;
}

function handleAsyncResponse(res: unknown[] | { data: unknown[]; total: number }): SelectMenuOption[] {
  if (Array.isArray(res)) {
    return res.map((option) => normalizeOption(option));
  }

  totalOptions.value = res.total;
  return res.data.map((option) => normalizeOption(option));
}

async function onAsyncOptionsLoad(options: AsyncOptionsLoadParams): Promise<SelectMenuOption[]> {
  if (typeof props.options !== "function") {
    console.error("Can't load async options since 'options' prop is not a function!");
    return Promise.resolve([]);
  }

  const q = options?.q;
  const offset = options?.offset;
  const limit = options?.limit || props.limit;
  const params: AsyncOptionsLoadParams = { q, offset, limit };

  return props
    .options(params)
    .then(handleAsyncResponse)
    .then((items) => {
      // If the offset is positive then we're assuming it's a pagination call and merging previous items with new
      if (offset > 0) {
        return asyncOptions.value.concat(items);
      }

      return items;
    });
}

function selectAllOptions() {
  if (!props.multiple) return;

  const value = filteredOptions.value.map((option) => guessOptionValue(option));
  emit("update:modelValue", value);
}

function toggleSelectAll() {
  if (isAllOptionsSelected.value) {
    clearSelection();
  } else {
    selectAllOptions();
  }
}

function onLoadMoreOptions() {
  executeAsyncOptionsLoad(0, { q: searchValue.value, offset: asyncOptions.value.length });
  emit("scroll", { q: searchValue.value });
}

function canLoadMore(): boolean {
  // For non-async search, don't allow loading more when filtering locally
  if (searchValue.value && !props.asyncSearch) return false;
  if (typeof props.options === "function") {
    return totalOptions.value > asyncOptions.value.length;
  }
  if (Array.isArray(props.options)) {
    return (props?.totalArrayOptions || 0) > props.options.length;
  }
  return false;
}

async function onMenuShow(event: FocusEvent) {
  onFocus(event);

  // we need to wait until the list is rendered to scroll the highlighted value into view
  await new Promise((resolve) => setTimeout(resolve, 100));
  scrollHighlightValueIntoView(false);
}

defineExpose({ executeAsyncOptionsLoad, menuRef, normalizedOptions });

onMounted(() => typeof props.options === "function" && executeAsyncOptionsLoad(0, { offset: 0 }));
// // @ts-expect-error `useDebounceFn` doesn't use generics, so `q` is set to any type and because of it we have a TS type conflict =(
watch(
  searchValue,
  useDebounceFn((q) => {
    if (props.asyncSearch) {
      executeAsyncOptionsLoad(0, { q, offset: 0 });
    }
    emit("search", { q, offset: 0 });
  }, props.searchDelay)
);
</script>

<template>
  <UMenu
    ref="menuRef"
    v-track="`select-menu-${props.ariaLabel || 'dropdown'}`"
    :class="props.variant === 'inline' ? '' : 'w-full'"
    :auto-hide="props.autoHide"
    :dropdown="dropdownConfig"
    :container-ref="props.renderInPortal ? undefined : `#${inputControlId}`"
    :disabled="props.disabled"
    :aria-label="props.ariaLabel"
    @show="onMenuShow"
    @hide="onBlur"
  >
    <template #trigger="{ shown, toggle, show, hide }">
      <UInputControl
        :id="inputControlId"
        :loading="isLoading"
        :disabled="props.disabled"
        :icon="props.icon"
        :leading-icon="(props.showSelectedOptionIcon && normalizedSelectedOptionIcon) || props.leadingIcon"
        :trailing-icon="getTrailingIcon(shown, isDisabled, props.variant, brandColor || '')"
        :trailing="props.trailing"
        :leading="props.leading"
        :has-error="props.hasError"
        :has-focus="hasFocus"
        :size="props.size"
        :variant="props.variant"
        :class="[brandColor, props.inputClass]"
      >
        <template v-if="$slots.leading" #leading="{ disabled: slotDisabled, loading: slotLoading }">
          <slot name="leading" :disabled="slotDisabled" :loading="slotLoading" />
        </template>

        <template v-if="$slots.trailing" #trailing="{ disabled: slotDisabled, loading: slotLoading }">
          <slot
            name="trailing"
            :disabled="slotDisabled"
            :loading="slotLoading"
            :shown="shown"
          />
        </template>

        <slot
          :toggle="toggle"
          :shown="!!shown"
          :show="show"
          :hide="hide"
        >
          <button
            :id="inputId"
            class="u-input-field text-left"
            :class="[props.inputInnerClass, props.truncateDisplayValue && 'truncate'].join(' ')"
            type="button"
            role="combobox"
            :aria-expanded="shown"
            :name="props.name || formGroup?.name.value"
            :placeholder="props.placeholder"
            :disabled="isDisabled"
            @click="toggle"
            @focus="onFocus"
            @blur="onBlur"
          >
            <slot name="selected-label" :normalized-label="normalizedLabel">
              <template v-if="normalizedLabel">
                <span>{{ normalizedLabel }}</span>
                <!-- Hidden selections counter badge -->
                <span
                  v-if="hiddenSelectionsCount > 0"
                  v-tooltip="hiddenSelectionsTooltip"
                  class="ms-2 inline-flex min-w-[36px] items-center justify-center rounded-xl border bg-neutral-light-50 px-2.5 py-0.5 text-sm leading-none dark:border-neutral-dark-500 dark:bg-neutral-dark-600"
                >+{{ hiddenSelectionsCount }}</span>
              </template>
              <span
                v-else-if="props.placeholder"
                :class="{ 'text-neutral-light-800 dark:text-neutral-dark-200': props.variant === 'default' }"
              >{{ props.placeholder }}
              </span>
              <span v-else>-</span>
            </slot>
          </button>
        </slot>
      </UInputControl>
    </template>

    <div>
      <div v-if="props.searchable" class="flex gap-4 bg-white p-4 dark:bg-neutral-dark-700">
        <UCheckbox
          v-if="props.multiple"
          :model-value="isAllOptionsSelected"
          name="select-all-options"
          :indeterminate="isAnyOptionSelected && !isAllOptionsSelected"
          :disabled="isDisabled || !filteredOptions.length"
          @update:model-value="toggleSelectAll()"
        />
        <UInput
          v-model="searchValue"
          :loading="isLoadingAsyncOptions"
          size="md"
          class="flex-1"
          :placeholder="t('labels.search')"
          icon="search"
          variant="filled"
          autofocus
        />
      </div>

      <UDivider v-if="props.searchable" lighter />

      <slot name="header" />

      <UList
        v-infinite-scroll="[onLoadMoreOptions, { canLoadMore }]"
        v-auto-animate
        class="max-h-64 !min-w-full overflow-auto"
        :class="`${props.listClass} ${props.variant === 'inline' && 'sm:max-w-72'}`"
      >
        <template v-if="props.multiple && !props.searchable && props.selectAllAsItemList">
          <UListItem
            :size="props.listItemSize"
            :text="t('labels.select_all')"
            @click="toggleSelectAll()"
          >
            <div class="flex flex-1 items-center gap-4">
              <UCheckbox
                :model-value="isAllOptionsSelected"
                name="select-all-options"
                :indeterminate="isAnyOptionSelected && !isAllOptionsSelected"
                @update:model-value="toggleSelectAll()"
              />
              <slot name="option">
                <span class="truncate">{{ t('labels.select_all') }}</span>
              </slot>
            </div>
          </UListItem>
        </template>

        <UListItem
          v-if="isAsyncOptionsError"
          :size="props.listItemSize"
          :has-hover="false"
          :on-click-close-menu="false"
          text="Error occurred during the search"
        />
        <UListItem
          v-for="(option, index) in filteredOptions"
          v-else-if="filteredOptions.length"
          :key="`${option[props.valueAttribute]}-${index}`"
          ref="listItems"
          :size="props.listItemSize"
          :value="option[props.valueAttribute]"
          :aria-selected="isOptionSelected(option)"
          :icon="option.icon"
          :disabled="option.disabled"
          :text="String(option[props.optionAttribute])"
          :level="option.level"
          :trailing-icon="!props.multiple && !hideTrailingIcon && isOptionSelected(option) ? { name: 'check', class: 'text-blue-500' } : null"
          :divider="(filteredOptions.length - 1) !== index && props.divider"
          :on-click="() => onOptionSelect(option)"
          :image="option.image"
          :container-class="containerClass"
          :class="[isOptionHighlighted(option) ? 'bg-neutral-light-100 dark:bg-neutral-dark-500' : '', itemClass]"
        >
          <template v-if="props.multiple" #pre-leading>
            <UCheckbox
              :model-value="isOptionSelected(option)"
              :name="`select-option-${index}-${option[props.valueAttribute]}`"
              :disabled="option.disabled"
              @update:model-value="() => onOptionSelect(option)"
              @click.stop
            />
          </template>
          <slot name="option" :option="option">
            <span class="truncate">{{ String(option[props.optionAttribute]) }}</span>
          </slot>
        </UListItem>
        <UListItem
          v-else-if="hasEmptyState"
          :size="props.listItemSize"
          :has-hover="false"
          :on-click-close-menu="false"
          text="The list is empty"
        />
        <UListItem
          v-if="isLoadingAsyncOptions || isLoadingOptions"
          :size="props.listItemSize"
          :has-hover="false"
          :on-click-close-menu="false"
          text="Loading..."
        />
      </UList>
    </div>
  </UMenu>
</template>
